import { createContext } from 'react'

import type { AuthenticatedUser, LoginOutcome } from '@/api/types'
import type { PermissionCode } from '@/lib/permissions'

/**
 * A challenge that stands between a correct password and a session.
 *
 * The token is short-lived, single-use, and authorises exactly one follow-up call. It is held in
 * memory like the access token, so abandoning the flow — a reload, a closed tab — abandons the
 * challenge rather than leaving a half-authenticated state behind.
 */
export interface PendingChallenge {
  kind: 'totp' | 'mfa_enrolment' | 'password_change'
  token: string
  username: string
  /** Why a password change was forced: first sign-in, administrator reset, policy breach. */
  reason?: string
}

export type AuthStatus = 'initialising' | 'anonymous' | 'authenticated'

export interface AuthContextValue {
  status: AuthStatus
  user: AuthenticatedUser | null
  challenge: PendingChallenge | null
  isAuthenticated: boolean

  /**
   * Applies the outcome of a sign-in step: establishes the session, or records the challenge the
   * user must clear next. Returns the outcome so the caller can route on it.
   */
  applyOutcome: (outcome: LoginOutcome, username: string) => LoginOutcome
  clearChallenge: () => void
  signOut: () => Promise<void>

  hasPermission: (permission: PermissionCode) => boolean
  hasAnyPermission: (permissions: readonly PermissionCode[]) => boolean
}

export const AuthContext = createContext<AuthContextValue | null>(null)
